import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo, useState } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Header,
  Separator,
  SelectIndicator,
  useOverlayState
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useRhinoContext, RhinoPathEntries } from '@rhino-project/core';
import { useBaseOwnerId, useAllowOrgCreation } from '@rhino-project/core/hooks';
import { useRouter } from '@tanstack/react-router';
import { OrganizationAvatar } from '../../OrganizationAvatar';
import { ModelCreateModal } from '../models/ModelCreateModal';

type OrgResource = { id: string | number };

const ORG_PATHS: RhinoPathEntries<'organization'> = ['name'];

export interface BaseOwnerSwitcherProps {
  /**
   * Custom handler for selection changes. Receives the selected organization id.
   * When omitted, navigates to `/$owner` for the chosen organization.
   */
  onChange?: (key: string) => void;
}

export const BaseOwnerSwitcher = ({
  onChange
}: BaseOwnerSwitcherProps = {}) => {
  const { t } = useTranslation();
  const { baseOwner, usersRoles } = useRhinoContext();
  const baseOwnerId = useBaseOwnerId();
  const allowCreate = useAllowOrgCreation();
  const router = useRouter();
  const createOrgState = useOverlayState();
  const [isOpen, setIsOpen] = useState(false);

  // The logo rides along on the base owner (session payload), the same place
  // as the org name — no separate fetch.
  const logoUrl = (
    baseOwner as { logo_attachment?: { url?: string } | null } | null
  )?.logo_attachment?.url;

  const allOrgs = useMemo(() => usersRoles ?? [], [usersRoles]);

  if (!baseOwner?.id || !usersRoles) return null;

  const owner = String(baseOwner.id);

  const handleSettingsClick = () =>
    router.navigate({ to: '/$owner/settings', params: { owner } });

  const handleOrgSwitch = (id: string) => {
    if (onChange) onChange(id);
    else router.navigate({ to: '/$owner', params: { owner: id } });
  };

  return (
    <>
      <Dropdown isOpen={isOpen} onOpenChange={setIsOpen}>
        <DropdownTrigger>
          <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-field bg-surface-secondary border border-border w-full text-left cursor-pointer">
            <OrganizationAvatar
              name={baseOwner.name as string}
              logoUrl={logoUrl}
              size="md"
            />
            <span className="flex-1 text-sm font-semibold text-foreground truncate">
              {baseOwner.name as string}
            </span>
            <SelectIndicator
              className={`text-muted shrink-0 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </DropdownTrigger>
        <DropdownPopover placement="bottom start">
          <DropdownMenu aria-label={t('rhino.frontend.messages.organization')}>
            <DropdownItem
              id="settings"
              key="settings"
              textValue={t('rhino.frontend.messages.settings')}
              onAction={handleSettingsClick}
            >
              <span className="flex items-center gap-2">
                <Icon icon="bi:gear" className="size-4" />
                <span>{t('rhino.frontend.messages.settings')}</span>
              </span>
            </DropdownItem>
            {allOrgs.length > 0 && <Separator />}
            {allOrgs.length > 0 && (
              <DropdownSection key="switch">
                <Header>{t('rhino.frontend.messages.switch_to')}</Header>
                {allOrgs.map((ur) => {
                  const isActive =
                    String(ur.organization.id) === String(baseOwnerId);
                  return (
                    <DropdownItem
                      key={ur.organization.id}
                      id={String(ur.organization.id)}
                      textValue={ur.organization.name as string}
                      onAction={() =>
                        handleOrgSwitch(String(ur.organization.id))
                      }
                    >
                      <span className="flex items-center gap-2 w-full">
                        <OrganizationAvatar
                          name={ur.organization.name as string}
                          logoUrl={
                            (
                              ur.organization as {
                                logo_attachment?: { url?: string } | null;
                              }
                            ).logo_attachment?.url
                          }
                          size="sm"
                        />
                        <span className="flex-1">
                          {ur.organization.name as string}
                        </span>
                        {isActive && (
                          <Icon
                            icon="bi:check"
                            className="size-4 text-accent shrink-0"
                          />
                        )}
                      </span>
                    </DropdownItem>
                  );
                })}
              </DropdownSection>
            )}
            {allowCreate && (
              <DropdownItem
                key="create"
                id="create"
                textValue={t('rhino.frontend.messages.create_organization')}
                onAction={createOrgState.open}
              >
                <span className="flex items-center gap-2 text-accent">
                  <Icon icon="bi:plus-circle" className="size-4" />
                  <span>
                    {t('rhino.frontend.messages.create_organization')}
                  </span>
                </span>
              </DropdownItem>
            )}
          </DropdownMenu>
        </DropdownPopover>
      </Dropdown>
      <ModelCreateModal
        model="organization"
        paths={ORG_PATHS}
        isOpen={createOrgState.isOpen}
        onModalClose={createOrgState.close}
        onSave={(created: OrgResource) => {
          router.navigate({
            to: '/$owner',
            params: { owner: String(created.id) }
          });
        }}
      />
    </>
  );
};
